import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Plus, Trash2, Sun, Moon, User, Building2,
  Calendar, Package, DollarSign, Printer, X, AlertCircle,
  Check, Loader2, ChevronRight, Receipt, RefreshCw
} from 'lucide-react';
import API_BASE_URL from './../../../config/baseApi';

export default function GuestBilling() {
  const navigate = useNavigate();
  
  const [theme, setTheme] = useState('light');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  const [rooms, setRooms] = useState([]);
  const [items, setItems] = useState([]);
  const [recentBills, setRecentBills] = useState([]);
  
  const [selectedRoom, setSelectedRoom] = useState('');
  const [guestName, setGuestName] = useState('');
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedItem, setSelectedItem] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  
  const [billItems, setBillItems] = useState([]);
  const [billNo, setBillNo] = useState(1000);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [notification, setNotification] = useState(null);

  // Show notification helper - MUST be after state declarations
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    fetchInitialData();
    const savedBillNo = localStorage.getItem('billNo');
    if (savedBillNo) {
      setBillNo(parseInt(savedBillNo, 10));
    }
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      const [roomsRes, itemsRes, billsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/billing/rooms`, {
          headers: { 'Accept': 'application/json' }
        }),
        fetch(`${API_BASE_URL}/api/billing/items`, {
          headers: { 'Accept': 'application/json' }
        }),
        fetch(`${API_BASE_URL}/api/billing/latest`, {
          headers: { 'Accept': 'application/json' }
        })
      ]);

      if (!roomsRes.ok || !itemsRes.ok || !billsRes.ok) {
        throw new Error('Failed to fetch data from server');
      }

      const roomsData = await roomsRes.json();
      const itemsData = await itemsRes.json();
      const billsData = await billsRes.json();

      if (roomsData.success) setRooms(roomsData.data || []);
      if (itemsData.success) setItems(itemsData.data || []);
      if (billsData.success) setRecentBills(billsData.data || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification('Error loading data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomChange = (roomNo) => {
    setSelectedRoom(roomNo);
    if (roomNo === 'Other Billing') {
      setGuestName('Other Billing');
    } else if (roomNo) {
      const room = rooms.find(r => r.room_no === roomNo);
      if (room && room.registration) {
        setGuestName(room.registration.guest_name);
      }
    } else {
      setGuestName('');
    }
  };

  const handleItemChange = (itemName) => {
    setSelectedItem(itemName);
    const item = items.find(i => i.item_name === itemName);
    if (item) {
      setPrice(item.price);
    }
  };

  const addItemToBill = () => {
    if (!selectedItem || !quantity || !price) {
      showNotification('Please fill in all fields', 'error');
      return;
    }

    if (!selectedRoom) {
      showNotification('Please select a room first', 'error');
      return;
    }

    const item = items.find(i => i.item_name === selectedItem);
    
    if (!item) {
      showNotification('Invalid item selected', 'error');
      return;
    }

    const newItem = {
      itemName: selectedItem,
      itemCode: item.item_code,
      itemCat: item.item_cat || 'R',
      qty: parseFloat(quantity),
      price: parseFloat(price),
      total: parseFloat(quantity) * parseFloat(price),
      billDate: billDate
    };

    setBillItems(prev => [...prev, newItem]);
    setSelectedItem('');
    setQuantity('');
    setPrice('');
    showNotification('Item added to bill', 'success');
  };

  const removeItem = (index) => {
    setBillItems(prev => prev.filter((_, i) => i !== index));
    showNotification('Item removed', 'info');
  };

  const getTotalBill = () => {
    return billItems.reduce((sum, item) => sum + item.total, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (billItems.length === 0) {
      showNotification('Please add at least one item to the bill', 'error');
      return;
    }

    if (!selectedRoom) {
      showNotification('Please select a room', 'error');
      return;
    }

    try {
      setProcessing(true);
      const currentTime = new Date().toLocaleTimeString();

      const formData = {
        allocated_room_no: selectedRoom,
        guest_name: guestName,
        bill_date: billDate,
        bill_no: billNo,
        bill_items: billItems,
        total_bill: getTotalBill()
      };

      const response = await fetch(`${API_BASE_URL}/api/billing/store`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to create bill');
      }

      printBill(formData, currentTime);
      
      const newBillNo = billNo + 1;
      setBillNo(newBillNo);
      localStorage.setItem('billNo', newBillNo);
      
      setBillItems([]);
      setSelectedRoom('');
      setGuestName('');
      setSelectedItem('');
      setQuantity('');
      setPrice('');
      
      await fetchInitialData();
      
      showNotification('Bill created successfully!', 'success');
    } catch (error) {
      console.error('Error creating bill:', error);
      showNotification('Error creating bill: ' + error.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const printBill = (formData, currentTime) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Bill - ${formData.bill_no}</title>
        <style>
          @media print {
            @page { margin: 0; }
            body { margin: 1cm; }
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0;
            padding: 0;
          }
          .container {
            max-width: 100%;
            width: 500px;
            padding: 20px;
            box-sizing: border-box;
            margin: 40px auto;
            border: 2px solid black;
          }
          h1 {
            text-align: center;
            font-weight: bold;
            margin: 20px 0;
            font-family: 'Georgia', serif;
            font-size: 28px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 20px 0;
          }
          table, th, td { 
            border: 1px solid black; 
            padding: 8px;
          }
          th {
            background-color: #f0f0f0;
            text-align: left;
            font-weight: bold;
          }
          .header-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
          }
          .left { 
            width: 50%; 
          }
          .right { 
            width: 50%; 
            text-align: right; 
          }
          .full-width-line { 
            border-top: 1px solid black; 
            margin: 4px 0; 
          }
          p {
            margin: 5px 0;
            line-height: 1.5;
          }
          .fw-bold {
            font-weight: bold;
          }
          .total {
            font-size: 18px;
            font-weight: bold;
            margin-top: 20px;
          }
          .signature {
            margin-top: 40px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <h1>Luxury Hotel</h1>
          <div class="header-section">
            <div class="left">
              <p class="fw-bold">Guest Billing: <span style="font-weight: normal;">Restaurant</span></p>
              <p class="fw-bold">Date: <span style="font-weight: normal;">${formData.bill_date}</span></p>
              <p class="fw-bold">Room No: <span class="fw-bold">${formData.allocated_room_no}</span></p>
              <p class="fw-bold">Guest Name: <span class="fw-bold">${formData.guest_name}</span></p>
            </div>
            <div class="right">
              <p>${currentTime}</p>
              <p class="fw-bold">Bill No: <span style="font-weight: normal;">${formData.bill_no}</span></p>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${formData.bill_items.map(item => `
                <tr>
                  <td>${item.itemName}</td>
                  <td>${item.qty}</td>
                  <td>Rs ${parseFloat(item.price).toFixed(2)}</td>
                  <td>Rs ${parseFloat(item.total).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <p class="total">Total Bill: Rs ${formData.total_bill.toFixed(2)}</p>
          <div class="full-width-line"></div>
          <div class="full-width-line"></div>
          <div class="signature">
            <p>................................</p>
            <p class="fw-bold">Guest Signature</p>
          </div>
        </div>
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
    } else {
      showNotification('Please allow popups to print', 'error');
    }
  };

  const handleDeleteBill = (billId, billNoToDelete) => {
    setDeleteTarget({ id: billId, billNo: billNoToDelete });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/${deleteTarget.billNo}/delete-all`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to delete bill');
      }

      showNotification('Bill deleted successfully', 'success');
      await fetchInitialData();
    } catch (error) {
      console.error('Error deleting bill:', error);
      showNotification('Error: ' + error.message, 'error');
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-blue-950 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-white/60">Loading billing system...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-blue-950 dark:to-slate-900 transition-colors duration-300">
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl backdrop-blur-xl border ${
            notification.type === 'success' 
              ? 'bg-emerald-500/90 border-emerald-400 text-white' 
              : notification.type === 'error'
              ? 'bg-red-500/90 border-red-400 text-white'
              : 'bg-blue-500/90 border-blue-400 text-white'
          }`}>
            {notification.type === 'success' && <Check className="w-5 h-5" />}
            {notification.type === 'error' && <AlertCircle className="w-5 h-5" />}
            {notification.type === 'info' && <AlertCircle className="w-5 h-5" />}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="max-w-[1800px] mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
          {/* Recent Bills - Left Side */}
          <div className="xl:col-span-5">
            <div className="bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-slate-900 dark:text-white text-lg font-semibold flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  Recent Bills (Today)
                </h3>
                <span className="px-3 py-1 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 text-sm font-medium">
                  {recentBills.length} bills
                </span>
              </div>
              
              <div className="overflow-x-auto" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <table className="w-full">
                  <thead className="sticky top-0 bg-slate-100 dark:bg-white/5 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Room</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Guest</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Bill No</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                    {recentBills.length > 0 ? (
                      recentBills.map((bill, index) => (
                        <tr key={bill.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{index + 1}</td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">{bill.key_room}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-white/60">
                            {new Date(bill.bill_date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{bill.guest_name}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-blue-600 dark:text-blue-400">{bill.bill_no}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleDeleteBill(bill.id, bill.bill_no)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-500/30 transition-colors"
                              title="Delete bill"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-4 py-12 text-center">
                          <Receipt className="w-12 h-12 text-slate-300 dark:text-white/20 mx-auto mb-3" />
                          <p className="text-slate-500 dark:text-white/40">No bills created today</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Billing Form - Right Side */}
          <div className="xl:col-span-7">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Room Selection */}
              <div className="bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-6 shadow-lg">
                <label className="text-slate-900 dark:text-white font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  Select Room
                </label>
                <select
                  value={selectedRoom}
                  onChange={(e) => handleRoomChange(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/50 focus:outline-none"
                  required
                >
                  <option value="">Select Room</option>
                  {rooms.map(room => (
                    <option key={room.room_no} value={room.room_no}>
                      {room.room_no} || {room.registration?.guest_name}
                    </option>
                  ))}
                  <option value="Other Billing">Other Billing</option>
                </select>

                {guestName && (
                  <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-violet-600/10 border border-blue-200 dark:border-blue-500/30">
                    <p className="text-slate-600 dark:text-white/60 text-sm mb-1">Guest Name</p>
                    <p className="text-slate-900 dark:text-white text-2xl font-bold">{guestName}</p>
                  </div>
                )}
              </div>

              {/* Item Selection */}
              <div className="bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-6 shadow-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-slate-600 dark:text-white/60 text-sm mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Bill Date
                    </label>
                    <input
                      type="date"
                      value={billDate}
                      onChange={(e) => setBillDate(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/50"
                      required
                    />
                  </div>

                  <div>
                    <label className="text-slate-600 dark:text-white/60 text-sm mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Item
                    </label>
                    <select
                      value={selectedItem}
                      onChange={(e) => handleItemChange(e.target.value)}
                      disabled={!selectedRoom}
                      className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Select Item</option>
                      {items.map(item => (
                        <option key={item.item_code} value={item.item_name}>
                          {item.item_code} - {item.item_name} - Rs {item.price}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Quantity</label>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addItemToBill();
                        }
                      }}
                      className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/50"
                      min="1"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Price (Rs)</label>
                    <input
                      type="number"
                      value={price}
                      readOnly
                      className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">&nbsp;</label>
                    <button
                      type="button"
                      onClick={addItemToBill}
                      disabled={!selectedItem || !quantity || !price}
                      className="w-full bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-5 h-5" />
                      Add Item
                    </button>
                  </div>
                </div>
              </div>

              {/* Bill Items Table */}
              <div className="bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-slate-900 dark:text-white text-lg font-semibold flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    Bill Items
                  </h3>
                  {billItems.length > 0 && (
                    <span className="px-3 py-1 rounded-lg bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 text-sm font-medium">
                      {billItems.length} {billItems.length === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 dark:bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Item Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Qty</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Price</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Date</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                      {billItems.map((item, index) => (
                        <tr key={index} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{index + 1}</td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">{item.itemName}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-white/60">{item.qty}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-white/60">Rs {parseFloat(item.price).toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-blue-600 dark:text-blue-400">Rs {item.total.toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-white/60">{item.billDate}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-500/30 transition-colors"
                              title="Remove item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {billItems.length === 0 && (
                    <div className="text-center py-12">
                      <ShoppingCart className="w-12 h-12 text-slate-300 dark:text-white/20 mx-auto mb-3" />
                      <p className="text-slate-500 dark:text-white/40">No items added yet</p>
                      <p className="text-slate-400 dark:text-white/30 text-sm mt-1">Select a room and add items to create a bill</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Total and Submit */}
              <div className="bg-gradient-to-br from-white/80 to-blue-50/80 dark:from-white/[0.07] dark:to-blue-500/[0.07] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-8 shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-slate-600 dark:text-white/60 text-sm mb-1">Total Bill Amount</p>
                    <p className="text-slate-900 dark:text-white text-4xl font-bold">
                      Rs {getTotalBill().toFixed(2)}
                    </p>
                    <p className="text-slate-500 dark:text-white/40 text-sm mt-1">Bill No: {billNo}</p>
                  </div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                    <DollarSign className="w-8 h-8 text-white" />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={processing || billItems.length === 0}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:from-slate-400 disabled:to-slate-500 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl shadow-xl transition-all flex items-center justify-center gap-3"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Printer className="w-5 h-5" />
                      <span>Print Bill</span>
                      <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-white/10 p-8 max-w-md w-full shadow-2xl animate-scale-in">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-slate-900 dark:text-white text-2xl font-bold mb-2">Are you sure?</h3>
              <p className="text-slate-600 dark:text-white/60 mb-6">
                This will delete bill #{deleteTarget?.billNo} and all its items. This action cannot be undone!
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/20 text-slate-900 dark:text-white font-semibold py-3 px-6 rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional CSS for animations */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
        
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}